import { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Badge, Button, Card, CardContent, Skeleton, cn } from '@respectly/ui';
import type { AiUsageCallRow } from '@respectly/shared-types';
import { useAiOpsStore } from '../../stores/useAiOpsStore';
import { CATEGORY_META, formatNumber, formatUsd } from './format';

export function CallsTab() {
  const { calls, loadingCalls, callsPage, callsHasMore, loadCalls } = useAiOpsStore();
  const [selected, setSelected] = useState<AiUsageCallRow | null>(null);

  return (
    <Card className="border-slate-200 dark:border-slate-800">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500 dark:border-slate-800">
                <th className="px-4 py-3 font-medium">Time</th>
                <th className="px-4 py-3 font-medium">Tenant</th>
                <th className="px-4 py-3 font-medium">Agent / action</th>
                <th className="px-4 py-3 font-medium">Model</th>
                <th className="px-4 py-3 text-right font-medium">Tokens (in/out)</th>
                <th className="px-4 py-3 text-right font-medium">Cost</th>
                <th className="px-4 py-3 text-right font-medium">Latency</th>
                <th className="px-4 py-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {loadingCalls && calls.length === 0 ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-b border-slate-100 dark:border-slate-800/50">
                    <td className="px-4 py-3" colSpan={8}><Skeleton className="h-5 w-full" /></td>
                  </tr>
                ))
              ) : calls.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-12 text-center text-slate-400">No AI calls in this range yet.</td>
                </tr>
              ) : (
                calls.map((c) => (
                  <tr
                    key={c.id}
                    onClick={() => setSelected(c)}
                    className="cursor-pointer border-b border-slate-100 transition-colors hover:bg-slate-50 dark:border-slate-800/50 dark:hover:bg-slate-800/40"
                  >
                    <td className="whitespace-nowrap px-4 py-2.5 text-slate-500">{new Date(c.createdAt).toLocaleString()}</td>
                    <td className="px-4 py-2.5">{c.tenantName ?? <span className="text-slate-400">Internal / Ops</span>}</td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: CATEGORY_META[c.category]?.color }} />
                        <span className="font-medium text-slate-700 dark:text-slate-200">{c.agentKey}</span>
                        {c.action && <span className="text-xs text-slate-400">{c.action}</span>}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-slate-600 dark:text-slate-300">{c.model}</td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums text-slate-600 dark:text-slate-300">
                      {formatNumber(c.promptTokens)}/{formatNumber(c.completionTokens)}
                    </td>
                    <td className="px-4 py-2.5 text-right font-mono tabular-nums font-medium text-slate-900 dark:text-slate-50">
                      {formatUsd(c.costUsd, { precise: true })}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{c.latencyMs != null ? `${c.latencyMs}ms` : '—'}</td>
                    <td className="px-4 py-2.5">
                      {c.success ? (
                        <Badge variant="outline" className="border-emerald-300 text-emerald-700 dark:text-emerald-400">OK</Badge>
                      ) : (
                        <Badge variant="outline" className="border-red-300 text-red-700 dark:text-red-400">Error</Badge>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm dark:border-slate-800">
          <span className="text-slate-500">Page {callsPage}</span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" disabled={callsPage <= 1 || loadingCalls} onClick={() => void loadCalls(callsPage - 1)}>
              <ChevronLeft className="h-4 w-4" /> Prev
            </Button>
            <Button variant="outline" size="sm" disabled={!callsHasMore || loadingCalls} onClick={() => void loadCalls(callsPage + 1)}>
              Next <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>

      {selected && <CallDrawer call={selected} onClose={() => setSelected(null)} />}
    </Card>
  );
}

function CallDrawer({ call, onClose }: { call: AiUsageCallRow; onClose: () => void }) {
  const rows: [string, string][] = [
    ['When', new Date(call.createdAt).toLocaleString()],
    ['Tenant', call.tenantName ?? 'Internal / Ops'],
    ['Category', CATEGORY_META[call.category]?.label ?? call.category],
    ['Feature', call.feature],
    ['Agent', call.agentKey],
    ['Action', call.action ?? '—'],
    ['Provider', call.provider],
    ['Model', call.model],
    ['Prompt tokens', formatNumber(call.promptTokens)],
    ['Completion tokens', formatNumber(call.completionTokens)],
    ['Total tokens', formatNumber(call.totalTokens)],
    ['Units', call.units != null ? `${call.units} ${call.unit ?? ''}` : '—'],
    ['Cost', formatUsd(call.costUsd, { precise: true })],
    ['Latency', call.latencyMs != null ? `${call.latencyMs} ms` : '—'],
    ['Token source', call.tokenSource],
    ['Status', call.success ? 'Success' : `Error${call.errorCode ? ` (${call.errorCode})` : ''}`],
    ['Correlation', call.correlationId ?? '—'],
    ['User', call.userId ?? call.superAdminUserId ?? '—'],
  ];
  return (
    <div className="fixed inset-0 z-50 flex justify-end" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative h-full w-full max-w-md overflow-y-auto border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-800">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-400">AI call</div>
            <div className="font-mono text-sm font-medium text-slate-900 dark:text-slate-50">{call.id}</div>
          </div>
          <button onClick={onClose} className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800">
            <X className="h-5 w-5" />
          </button>
        </div>
        <dl className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4 px-5 py-2.5 text-sm">
              <dt className="text-slate-500 dark:text-slate-400">{k}</dt>
              <dd className={cn('text-right font-medium text-slate-800 dark:text-slate-100', /token|Cost|Latency/.test(k) && 'font-mono tabular-nums')}>
                {v}
              </dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}
